import { PUBLIC_SITE_URL } from "@/config/site";
import { ROUTE_PATHS } from "@/routes/routePaths";
import type { BookingDetails } from "@/types/bookingDetails";
import type { RideDetails, RideDetailsLocation } from "@/types/rideDetails";
import { getCurrencySymbol } from "@/utils/currency";
import { extractLocalityFromAddress } from "@/utils/location/extractLocality";
import { joinShareBlocks, SHARE_ICONS } from "@/utils/share/shareFormat";

const SHARE_FOOTER = `DuriApp.com - The Smart Way to Commute ${SHARE_ICONS.car}`;

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName.trim();
}

/** Locality for share text — parsed from full address (`sl`), not short city (`sc`). */
function resolveShareLocality(location: RideDetailsLocation): string {
  return extractLocalityFromAddress(location.address);
}

function formatRouteLine(
  from: RideDetailsLocation,
  to: RideDetailsLocation
): string {
  return `${SHARE_ICONS.pin} ${resolveShareLocality(from)} \u2192 ${resolveShareLocality(to)}`;
}

function buildShareUrl(path: string, query: Record<string, string>): string {
  const params = new URLSearchParams(query);
  return `${PUBLIC_SITE_URL}${path}?${params.toString()}`;
}

export function buildRideShareMessage(ride: RideDetails): string {
  const name = firstName(ride.host.name);
  const symbol = getCurrencySymbol(ride.pricing.currency);
  const link = buildShareUrl(ROUTE_PATHS.RIDE_INFO, { rideId: ride.rideId });

  return joinShareBlocks(
    [`${SHARE_ICONS.wave} Hi, this is ${name}.`],
    [
      `I've posted my ride on DuriApp and (${ride.seats.available}) seats available.`,
    ],
    [
      `${SHARE_ICONS.id} Ride ID: ${ride.rideId}`,
      formatRouteLine(ride.route.pickup, ride.route.drop),
      `${SHARE_ICONS.calendar} ${ride.date} | ${SHARE_ICONS.clock} ${ride.time}`,
      `${SHARE_ICONS.money} ${symbol}${ride.pricing.pricePerSeat} / seat`,
    ],
    ["Just tap below to see the ride details and book instantly."],
    [`${SHARE_ICONS.point} BOOK NOW`, link],
    [SHARE_FOOTER]
  );
}

export interface BookingShareOptions {
  /** Shown as price per seat when the API exposes it. */
  pricePerSeat?: number;
  currency?: string;
}

export function buildBookingShareMessage(
  booking: BookingDetails,
  options: BookingShareOptions = {}
): string {
  const name = firstName(booking.guestName);
  const link = buildShareUrl(ROUTE_PATHS.BOOKING_INFO, {
    bookingId: booking.bookingId,
  });

  const detailsBlock = [
    `${SHARE_ICONS.id} Booking ID: ${booking.bookingId}`,
    formatRouteLine(booking.route.pickup, booking.route.drop),
    `${SHARE_ICONS.calendar} ${booking.date} | ${SHARE_ICONS.clock} ${booking.time}`,
  ];

  if (options.pricePerSeat != null) {
    const symbol = getCurrencySymbol(options.currency);
    detailsBlock.push(
      `${SHARE_ICONS.money} ${symbol}${options.pricePerSeat} / seat`
    );
  }

  return joinShareBlocks(
    [`${SHARE_ICONS.wave} Hi, this is ${name}.`],
    ["I've booked my ride on DuriApp."],
    detailsBlock,
    ["Just tap below to see booking details and live tracking."],
    [`${SHARE_ICONS.point} VIEW BOOKING`, link],
    [SHARE_FOOTER]
  );
}
